import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaArrowLeft, FaDoorOpen } from 'react-icons/fa';
import { previewIds } from '../../../../services/onboardingWizardService';
import { confirmSession } from '../../../../services/onboardingConfirmService';

function Step5Confirm({ session, pins, categories, propertyId }) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState({ rows: [], property_code: '' });
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await previewIds(session.id); setPreview(r || { rows: [], property_code: '' }); }
      catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, [session.id]);

  const countByCat = useMemo(() => {
    const c = {};
    for (const p of pins) { if (p.category_id) c[p.category_id] = (c[p.category_id] || 0) + 1; }
    return c;
  }, [pins]);

  const idsByCat = useMemo(() => {
    const c = {};
    for (const row of preview.rows || []) {
      if (!row.category_id || !row.generated_asset_id) continue;
      (c[row.category_id] ||= []).push(row.generated_asset_id);
    }
    return c;
  }, [preview]);

  const doConfirm = async () => {
    if (!window.confirm(`Create ${pins.length} assets on this property?\n\nThis creates permanent asset records and cannot be undone.`)) return;
    setConfirming(true);
    try {
      const r = await confirmSession(session.id);
      setResult(r);
      toast.success(`${r.confirmation?.created_count || 0} assets created`);
    } catch (e) { toast.error(e.message || 'Confirmation failed'); }
    finally { setConfirming(false); }
  };

  // Success state
  if (result) {
    return (
      <div className="ow-step-body">
        <section className="ow-card ow-success">
          <div className="ow-success-icon"><FaCheckCircle /></div>
          <h2 className="ow-card-title">Onboarding complete</h2>
          <p className="ow-card-sub">
            {result.confirmation?.created_count || 0} assets created on this property.
            The annotated plan is now available in the <strong>Drawings</strong> section.
          </p>
          <div className="ow-success-stats">
            {categories.map(c => (
              <div key={c.id} className="ow-success-stat">
                <span className="ow-cat-swatch" style={{ background: c.color }} />
                <strong>{countByCat[c.id] || 0}</strong>
                <em>{c.name}</em>
              </div>
            ))}
          </div>
          <div className="ow-success-actions">
            <button className="ow-btn ow-btn-ghost" onClick={() => navigate(`/propertydashboard/${propertyId}`)}>
              <FaDoorOpen /> Return to property
            </button>
            <button className="ow-btn ow-btn-primary" onClick={() => navigate(`/propertydashboard/${propertyId}/drawings`)}>
              View annotated plan
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="ow-step-body">
      <section className="ow-card">
        <header className="ow-card-head">
          <div>
            <div className="ow-card-kicker">Step 5 · Confirm</div>
            <h2 className="ow-card-title">Review the summary and create the assets</h2>
            <p className="ow-card-sub">
              This will create <strong>{pins.length} assets</strong> across {categories.length} categor{categories.length === 1 ? 'y' : 'ies'},
              assign each a permanent ID using the <code>{preview.property_code || '—'}</code> ID prefix,
              and publish the annotated plan to <strong>Drawings</strong>.
              The operation is atomic — nothing is created if any part fails.
            </p>
          </div>
        </header>

        <div className="ow-summary-grid">
          {categories.map(cat => {
            const ids = (idsByCat[cat.id] || []);
            return (
              <article key={cat.id} className="ow-summary-item">
                <header>
                  <span className="ow-cat-swatch" style={{ background: cat.color }} />
                  <div>
                    <strong>{cat.name}</strong>
                    <em>{cat.target_asset_type_key.replace(/_/g, ' ')}</em>
                  </div>
                  <span className="ow-cat-count">{countByCat[cat.id] || 0}</span>
                </header>
                {ids.length > 0 && (
                  <div className="ow-summary-ids">
                    {ids.slice(0, 6).map(id => <code key={id}>{id}</code>)}
                    {ids.length > 6 && <span className="ow-summary-more">+{ids.length - 6} more</span>}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <footer className="ow-card-foot">
          <button className="ow-btn ow-btn-ghost" onClick={() => window.history.back()}><FaArrowLeft /> Back</button>
          <button className="ow-btn ow-btn-primary ow-btn-lg"
                  disabled={loading || confirming || !pins.length}
                  onClick={doConfirm}>
            {confirming ? 'Creating assets…' : (<><FaCheckCircle /> Confirm and create {pins.length} assets</>)}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default Step5Confirm;
